/**
 * Hiring flows and the rules for moving a candidate through one (recruitment gap analysis bug 12 and
 * the Keka "Hiring Flow" gap). Pure on purpose, like `can()`: the API enforces these and the web
 * board reads the same functions to explain a refused drag before it is sent, so the two cannot
 * disagree.
 *
 * A flow is an ordered list of NAMED stages, each with one of the seven stage TYPES the rest of the
 * module already runs on (`CANDIDATE_STAGES`). `Candidate.stage` stays the type, so analytics, offers
 * and the hand-off keep working unchanged; a flow only adds names and extra stages of the middle
 * types ("Technical round", "HR round" are both `interview`).
 */

import { CANDIDATE_STAGES, type CandidateStage } from '../enums.js';

export const STAGE_TYPE_LABELS: Readonly<Record<CandidateStage, string>> = {
  applied: 'Applied',
  screening: 'Screening',
  interview: 'Interview',
  assessment: 'Assessment',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
};

/** The types a recruiter can add any number of, in any order, between Applied and Hired. */
export const MIDDLE_STAGE_TYPES = ['screening', 'interview', 'assessment', 'offer'] as const;

export interface FlowStageInput {
  readonly name: string;
  readonly type: CandidateStage;
}

/** What a new organisation starts with: today's seven stages, unchanged. */
export const DEFAULT_FLOW_STAGES: readonly FlowStageInput[] = CANDIDATE_STAGES.map((type) => ({
  name: STAGE_TYPE_LABELS[type],
  type,
}));

/**
 * Why this list of stages is not a valid flow, or null. The shape is fixed at both ends so every
 * candidate has somewhere to start and finish: exactly one Applied first, then any mix of
 * Screening / Interview / Assessment / Offer, then Hired, then Rejected.
 */
export function flowStagesProblem(stages: readonly FlowStageInput[]): string | null {
  if (stages.length < 3) return 'A hiring flow needs at least Applied, Hired and Rejected';
  if (stages.length > 20) return 'A hiring flow can have at most 20 stages';
  if (stages[0]!.type !== 'applied') return 'The first stage must be of type Applied';
  const last = stages.slice(-2).map((s) => s.type);
  if (last[0] !== 'hired' || last[1] !== 'rejected') {
    return 'The last two stages must be Hired, then Rejected';
  }
  for (const stage of stages.slice(1, -2)) {
    if (!(MIDDLE_STAGE_TYPES as readonly string[]).includes(stage.type)) {
      return `"${stage.name}" is a ${STAGE_TYPE_LABELS[stage.type]} stage; only one is allowed, at its fixed position`;
    }
  }
  const names = stages.map((s) => s.name.trim().toLowerCase());
  if (names.some((n) => n.length === 0)) return 'Every stage needs a name';
  if (new Set(names).size !== names.length) return 'Two stages have the same name';
  return null;
}

export type StageMoveRefusal =
  | 'withdrawn'
  | 'from_hired'
  | 'from_rejected'
  | 'to_rejected'
  | 'to_hired'
  | 'same_stage';

export const STAGE_MOVE_MESSAGES: Readonly<Record<StageMoveRefusal, string>> = {
  withdrawn: 'This candidate has withdrawn',
  from_hired: 'A hired candidate has joined; they cannot be moved back into the pipeline',
  from_rejected: 'Use Reconsider to reopen a rejected candidate',
  to_rejected: 'Use Reject so the reason is recorded and open interviews and offers are closed',
  to_hired: 'A candidate becomes Hired when they accept their offer',
  same_stage: 'The candidate is already in this stage',
};

/**
 * May a recruiter move a candidate between these stages by hand? Hired, Rejected and withdrawn are
 * all decided elsewhere (an accepted offer, the Reject action, the candidate), so "move" only ever
 * travels between the open stages — forwards or backwards, freely.
 */
export function stageMoveRefusal(input: {
  fromType: CandidateStage;
  toType: CandidateStage;
  sameStage: boolean;
  withdrawn: boolean;
}): StageMoveRefusal | null {
  if (input.withdrawn) return 'withdrawn';
  if (input.fromType === 'hired') return 'from_hired';
  if (input.fromType === 'rejected') return 'from_rejected';
  if (input.toType === 'rejected') return 'to_rejected';
  if (input.toType === 'hired') return 'to_hired';
  if (input.sameStage) return 'same_stage';
  return null;
}

// ---------------------------------------------------------------------------
// Candidate email merge fields
// ---------------------------------------------------------------------------

/** What a recruiter can put in an email as `{{token}}`. Filled in per candidate when it is sent. */
export const CANDIDATE_EMAIL_TOKENS = [
  { token: 'candidate_first_name', label: "Candidate's first name" },
  { token: 'candidate_name', label: "Candidate's full name" },
  { token: 'job_title', label: 'Job title' },
  { token: 'company_name', label: 'Company name' },
  { token: 'recruiter_name', label: 'Your name' },
  { token: 'recruiter_email', label: 'Your email' },
] as const;
export type CandidateEmailToken = (typeof CANDIDATE_EMAIL_TOKENS)[number]['token'];

const KNOWN_TOKENS: ReadonlySet<string> = new Set(CANDIDATE_EMAIL_TOKENS.map((t) => t.token));
const TOKEN_PATTERN = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Merge fields used in `text` that are not ones we fill in — they would go out blank. */
export function unknownEmailTokens(text: string): string[] {
  const unknown = new Set<string>();
  for (const match of text.matchAll(TOKEN_PATTERN)) {
    if (!KNOWN_TOKENS.has(match[1]!)) unknown.add(match[1]!);
  }
  return [...unknown];
}

/** Fills `{{tokens}}`; a token we don't know is left as typed, so it is visible, never blank. */
export function fillEmailTokens(
  text: string,
  values: Readonly<Record<CandidateEmailToken, string>>,
): string {
  return text.replace(TOKEN_PATTERN, (whole, token: string) =>
    KNOWN_TOKENS.has(token) ? values[token as CandidateEmailToken] : whole,
  );
}
